import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from 'axios';
import { AUTH_ENDPOINTS } from '../api/endpoints';
import {
  AUTH_NOTICE_PASSWORD_CHANGED,
  clearAuthData,
  getAuthNotice,
} from './logoutUtil';
import { ensureCsrfToken, clearCsrfToken } from './csrf';

export type ParsedApiError = {
  message: string;
  status?: number;
  fieldErrors?: Record<string, string>;
};

/** Prevents duplicate session-expired toasts when refresh 401 is handled by parallel requests. */
let isHandlingSessionExpiry = false;

/** Skip the generic "session expired" toast after an intentional logout (e.g. password change). */
export function beginIntentionalLogout(): void {
  isHandlingSessionExpiry = true;
}

/** Restore normal 401 handling after a failed password change or successful login. */
export function cancelIntentionalLogout(): void {
  isHandlingSessionExpiry = false;
}

/**
 * Shared in-flight refresh promise across all BaseApi instances.
 * Concurrent 401s must await the same POST /auth/refresh/ — refresh tokens rotate
 * on every use; a second concurrent refresh would revoke the session.
 */
let refreshPromise: Promise<void> | null = null;

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

function refreshSession(api: AxiosInstance): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = api
      .post(AUTH_ENDPOINTS.REFRESH_TOKEN, {})
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

class BaseApi {
  private api: AxiosInstance;
  protected baseUrl: string;

  constructor(baseURL: string) {
    this.baseUrl = baseURL;
    this.api = axios.create({
      baseURL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private isPublicAuthEndpoint(url?: string): boolean {
    if (!url) return false;

    const publicPaths = [
      AUTH_ENDPOINTS.SIGN_IN,
      AUTH_ENDPOINTS.SIGN_UP,
      AUTH_ENDPOINTS.FORGOT_PASSWORD,
      AUTH_ENDPOINTS.VERIFY_OTP,
      AUTH_ENDPOINTS.SET_PASSWORD,
      AUTH_ENDPOINTS.CHANGE_PASSWORD,
      AUTH_ENDPOINTS.VERIFY_EMAIL,
      AUTH_ENDPOINTS.CSRF,
      AUTH_ENDPOINTS.LOGOUT,
    ];

    return publicPaths.some((path) => url.includes(path));
  }

  private setupInterceptors() {
    this.api.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // Cookie auth only — never attach Authorization / Bearer headers.
        if (config.headers) {
          delete config.headers.Authorization;
          delete config.headers.authorization;
        }

        config.withCredentials = true;

        // If sending FormData, let Axios set the correct multipart boundary
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          try {
            delete config.headers['Content-Type'];
            delete config.headers['content-type'];
          } catch {
            // ignore
          }
        }

        const method = (config.method || 'get').toLowerCase();
        const isCsrfEndpoint = config.url?.includes(AUTH_ENDPOINTS.CSRF);

        if (MUTATING_METHODS.has(method) && !isCsrfEndpoint) {
          const token = await ensureCsrfToken(this.api);
          const headers = AxiosHeaders.from(config.headers);
          headers.set('X-CSRFToken', token);
          config.headers = headers;
        }

        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        const csrfFailed =
          error.response?.status === 403 &&
          String(
            (error.response?.data as { detail?: string } | undefined)?.detail || ''
          )
            .toLowerCase()
            .includes('csrf');
        if (csrfFailed) {
          clearCsrfToken();
        }

        const isRefreshEndpoint = originalRequest.url?.includes(AUTH_ENDPOINTS.REFRESH_TOKEN);

        // Refresh itself 401'd — cookies cleared server-side; end the session.
        if (error.response?.status === 401 && isRefreshEndpoint) {
          this.handleSessionExpired();
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry && !isRefreshEndpoint) {
          // Login/register failures should surface to the caller, not trigger session expiry
          if (this.isPublicAuthEndpoint(originalRequest.url)) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            await refreshSession(this.api);
            return this.api(originalRequest);
          } catch (refreshError) {
            // Refresh 401 already triggers handleSessionExpired via isRefreshEndpoint.
            // Network / other failures still need a clean logout redirect.
            if (!isHandlingSessionExpiry) {
              this.handleSessionExpired();
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private handleSessionExpired(): void {
    if (isHandlingSessionExpiry) {
      return;
    }
    isHandlingSessionExpiry = true;

    if (getAuthNotice() === AUTH_NOTICE_PASSWORD_CHANGED) {
      clearAuthData('password_changed');
      return;
    }

    clearAuthData();

    window.dispatchEvent(
      new CustomEvent('auth:sessionExpired', {
        detail: {
          message: 'Your session expired. Please login again.',
        },
      })
    );

    window.setTimeout(() => {
      isHandlingSessionExpiry = false;
    }, 2000);
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.api.get(url, config);
      return response.data;
    } catch (error) {
      throw this.parseError(error as AxiosError);
    }
  }

  protected async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
      let finalConfig = config;
      if (typeof FormData !== 'undefined' && data instanceof FormData) {
        const headers = AxiosHeaders.from(
          finalConfig?.headers as unknown as AxiosHeaders | undefined
        );
        headers.delete('Content-Type');
        headers.delete('content-type');
        finalConfig = { ...(finalConfig || {}), headers };
      }
      const response: AxiosResponse<T> = await this.api.post(url, data, finalConfig);
      return response.data;
    } catch (error) {
      throw this.parseError(error as AxiosError);
    }
  }

  protected async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.api.put(url, data, config);
      return response.data;
    } catch (error) {
      throw this.parseError(error as AxiosError);
    }
  }

  protected async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
      let finalConfig = config;
      if (typeof FormData !== 'undefined' && data instanceof FormData) {
        const headers = AxiosHeaders.from(
          finalConfig?.headers as unknown as AxiosHeaders | undefined
        );
        headers.delete('Content-Type');
        headers.delete('content-type');
        finalConfig = { ...(finalConfig || {}), headers };
      }
      const response: AxiosResponse<T> = await this.api.patch(url, data, finalConfig);
      return response.data;
    } catch (error) {
      throw this.parseError(error as AxiosError);
    }
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.api.delete(url, config);
      return response.data;
    } catch (error) {
      throw this.parseError(error as AxiosError);
    }
  }

  private parseError(error: AxiosError): ParsedApiError {
    const data = error?.response?.data as Record<string, unknown> | undefined;
    const status = error?.response?.status;
    const fieldErrors: Record<string, string> = {};

    if (data && typeof data === 'object') {
      for (const key in data) {
        const val = data[key];
        if (Array.isArray(val)) fieldErrors[key] = val.join(', ');
        else if (typeof val === 'string') fieldErrors[key] = val;
      }
    }

    const message =
      (data?.detail as string) ||
      (data?.message as string) ||
      Object.entries(fieldErrors)
        .filter(([key]) => key !== 'detail' && key !== 'message')
        .map(([, msg]) => msg)
        .join(' ') ||
      Object.entries(fieldErrors)
        .map(([key, msg]) => `${key}: ${msg}`)
        .join(' | ') ||
      'Something went wrong.';

    return { message, status, fieldErrors };
  }
}

export default BaseApi;
